import { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Typography,
  Space,
  Button,
  message,
  Spin,
  Upload,
  Empty,
  Alert,
  Tag,
  Divider,
} from 'antd';
import { UploadOutlined, ArrowLeftOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import request from '@/utils/request';
import { useAuthStore } from '@/stores/auth.store';
import { formatCents } from '@/utils/format';
import { thumb } from '@/utils/image';
import type { BundleDetailDTO } from '@zero/shared';

interface ResourceRow {
  id: number;
  imageId: number;
  label: string | null;
  image: { id: number; accessUrl: string };
}

const UsageRunPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bundleId = Number(searchParams.get('bundleId'));
  const creator = useAuthStore((s) => s.creator);

  const [bundle, setBundle] = useState<BundleDetailDTO | null>(null);
  const [resources, setResources] = useState<ResourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!Number.isFinite(bundleId) || bundleId <= 0) {
      message.error('缺少 bundleId');
      navigate('/bundles');
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const [bundleRes, resRes] = await Promise.all([
          request.get<BundleDetailDTO>(`/creator/bundles/${bundleId}`),
          request.get<{ items: ResourceRow[] }>('/creator/resources?pageSize=100'),
        ]);
        setBundle(bundleRes.data as unknown as BundleDetailDTO);
        setResources((resRes.data as unknown as { items: ResourceRow[] }).items);
      } finally {
        setLoading(false);
      }
    })();
  }, [bundleId, navigate]);

  const uploadProps = {
    name: 'file',
    showUploadList: false,
    customRequest: async ({ file, onSuccess, onError }: any) => {
      try {
        const realFile: File = file;
        const presign = await request.post<{ uploadUrl: string; cosKey: string }>('/cos/upload-url', {
          filename: realFile.name,
          contentType: realFile.type,
        });
        const presignData = presign.data as unknown as { uploadUrl: string; cosKey: string };
        await fetch(presignData.uploadUrl, {
          method: 'PUT',
          body: realFile,
          headers: { 'Content-Type': realFile.type },
        });
        const confirm = await request.post<{ id: number }>('/cos/confirm-upload', {
          cosKey: presignData.cosKey,
          size: realFile.size,
          mimeType: realFile.type,
        });
        const confirmData = confirm.data as unknown as { id: number };
        const res = await request.post<ResourceRow>('/creator/resources', {
          imageId: confirmData.id,
          label: realFile.name,
          kind: 'user_input',
        });
        const row = res.data as unknown as ResourceRow;
        setResources((prev) => [row, ...prev]);
        setSelected((prev) => (prev.length < (bundle?.maxUploadCount ?? 1) ? [...prev, row.id] : prev));
        onSuccess?.(row);
        message.success('上传成功');
      } catch (e) {
        onError?.(e as Error);
        message.error(e instanceof Error ? e.message : '上传失败');
      }
    },
  };

  const toggle = (resId: number) => {
    if (!bundle) return;
    setSelected((prev) => {
      if (prev.includes(resId)) return prev.filter((i) => i !== resId);
      if (prev.length >= bundle.maxUploadCount) {
        message.warning(`该套图最多 ${bundle.maxUploadCount} 张输入图`);
        return prev;
      }
      return [...prev, resId];
    });
  };

  const chargeCents = useMemo(() => {
    if (!bundle || !bundle.totalFalCostUsd) return 0;
    const rmb = Number(bundle.totalFalCostUsd) * 7.3 * 100;
    return Math.ceil(rmb);
  }, [bundle]);

  const onSubmit = async () => {
    if (!bundle) return;
    if (selected.length < bundle.requiredUploadCount) {
      message.warning(`至少选 ${bundle.requiredUploadCount} 张图片`);
      return;
    }
    if ((creator?.experimentBalanceCents ?? 0) < chargeCents) {
      message.error('实验金不足，请先充值');
      return;
    }
    setSubmitting(true);
    try {
      const res = await request.post<{ id: number }>('/creator/bundle-runs', {
        bundleId,
        sourceResourceIds: selected,
      });
      const data = res.data as unknown as { id: number };
      message.success('已启动试跑');
      navigate(`/usage/${data.id}`);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '启动失败');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !bundle) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>;
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          返回
        </Button>
        <Typography.Title level={3} style={{ margin: 0 }}>
          运行套图：{bundle.title}
        </Typography.Title>
      </Space>

      <Alert
        type="info"
        showIcon
        message={
          <>
            每次运行将生成 <b>{bundle.instructions.length}</b> 张图（按成本原价扣实验金）。
            预计扣费：
            <b style={{ color: '#D97706', marginLeft: 4 }}>{formatCents(chargeCents)}</b>
          </>
        }
      />

      <Card title={`上传输入图（${bundle.requiredUploadCount}–${bundle.maxUploadCount} 张）`}>
        {bundle.uploadInstruction && (
          <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
            {bundle.uploadInstruction}
          </Typography.Paragraph>
        )}
        <Upload {...uploadProps} accept="image/*">
          <Button icon={<UploadOutlined />}>新增上传到资源库</Button>
        </Upload>
        <Divider />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          可以点击下方资源库中的图片快速选用（最多 {bundle.maxUploadCount} 张）
        </Typography.Text>
        <div
          style={{
            marginTop: 12,
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: 10,
          }}
        >
          {resources.length === 0 ? (
            <div style={{ gridColumn: '1 / -1' }}>
              <Empty description="资源库为空，请先上传" />
            </div>
          ) : (
            resources.map((r) => {
              const isSelected = selected.includes(r.id);
              const idx = selected.indexOf(r.id);
              return (
                <div
                  key={r.id}
                  onClick={() => toggle(r.id)}
                  style={{
                    position: 'relative',
                    aspectRatio: '1/1',
                    borderRadius: 8,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: isSelected ? '2px solid #D97706' : '2px solid transparent',
                    boxShadow: isSelected ? '0 0 0 2px rgba(217,119,6,.18)' : undefined,
                  }}
                >
                  <img
                    src={thumb(r.image.accessUrl, 120)}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  {isSelected && (
                    <Tag
                      color="gold"
                      style={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        margin: 0,
                      }}
                    >
                      {idx + 1}
                    </Tag>
                  )}
                </div>
              );
            })
          )}
        </div>
        <Divider />
        <Space>
          <Typography.Text>
            已选 <b>{selected.length}</b> 张
          </Typography.Text>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={submitting}
            disabled={selected.length < bundle.requiredUploadCount}
            onClick={onSubmit}
          >
            开始生成 · 扣 {formatCents(chargeCents)}
          </Button>
        </Space>
      </Card>
    </Space>
  );
};

export default UsageRunPage;
